/**
 * Parse a simple CSV string into an array of header-keyed string records.
 *
 * Assumes no embedded commas, no quoted fields, no escapes — matches the
 * S3 CSV exports we consume (agb, growth_curves, soc). Throws on a
 * column-count mismatch so a stray unescaped comma surfaces loudly instead
 * of silently shifting values across columns.
 *
 * Strips trailing \r so files authored on Windows or by Python's csv writer
 * with `newline=''` parse cleanly. Caller is responsible for casting numeric
 * columns — this returns plain strings.
 */
export const parseCsv = (text: string): Record<string, string>[] => {
  const lines = text.split('\n').map(l => l.replace(/\r$/, ''));
  const nonEmpty = lines.filter(l => l.length > 0);
  if (nonEmpty.length < 2) return [];

  const headers = nonEmpty[0].split(',').map(h => h.trim());

  return nonEmpty.slice(1).map((line, idx) => {
    const cells = line.split(',').map(c => c.trim());
    if (cells.length !== headers.length) {
      throw new Error(
        `CSV row ${idx + 1}: expected ${headers.length} columns, got ${cells.length}. ` +
        `A value may contain an unescaped comma.`,
      );
    }
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = cells[i];
    });
    return row;
  });
};
